class SeedHeader extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section') || this.parentElement || this;
    this.abortController = new AbortController();
    this.lastScrollTop = this.readScrollTop();
    this.lastTouchY = null;
    this.rafId = null;
    this.details = [...this.querySelectorAll('details')];
    const options = { passive: true, signal: this.abortController.signal };

    this.syncExpandedState = (details) => {
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(details.open));
    };

    this.closeMenus = (except = null) => {
      this.details.forEach((details) => {
        if (details === except) return;
        details.removeAttribute('open');
        this.syncExpandedState(details);
      });
    };

    this.show = () => {
      this.section.classList.remove('seed-header-section--hidden');
      this.setAttribute('data-visible', 'true');
    };

    this.hide = () => {
      this.closeMenus();
      this.section.classList.add('seed-header-section--hidden');
      this.setAttribute('data-visible', 'false');
    };

    this.applyDirection = (direction, scrollTop = this.readScrollTop()) => {
      if (scrollTop < 24 || direction === 'up') this.show();
      else if (direction === 'down' && scrollTop > 76) this.hide();
      this.classList.toggle('seed-header--scrolled', scrollTop > 24);
    };

    this.handleScroll = () => {
      if (this.rafId !== null) return;
      this.rafId = requestAnimationFrame(() => {
        const scrollTop = this.readScrollTop();
        const delta = scrollTop - this.lastScrollTop;
        if (Math.abs(delta) >= 2) this.applyDirection(delta > 0 ? 'down' : 'up', scrollTop);
        else if (scrollTop < 24) this.show();
        this.lastScrollTop = scrollTop;
        this.rafId = null;
      });
    };

    this.handleWheel = (event) => {
      if (Math.abs(event.deltaY) >= 2) this.applyDirection(event.deltaY > 0 ? 'down' : 'up');
    };

    this.handleTouchStart = (event) => {
      this.lastTouchY = event.touches[0]?.clientY ?? null;
    };

    this.handleTouchMove = (event) => {
      const currentY = event.touches[0]?.clientY;
      if (currentY == null || this.lastTouchY == null) return;
      const delta = currentY - this.lastTouchY;
      if (Math.abs(delta) >= 3) this.applyDirection(delta > 0 ? 'up' : 'down');
      this.lastTouchY = currentY;
    };

    document.addEventListener('scroll', this.handleScroll, { ...options, capture: true });
    window.addEventListener('scroll', this.handleScroll, options);
    window.addEventListener('wheel', this.handleWheel, options);
    window.addEventListener('touchstart', this.handleTouchStart, options);
    window.addEventListener('touchmove', this.handleTouchMove, options);
    window.addEventListener('mousemove', (event) => {
      if (event.clientY <= 12) this.show();
    }, options);
    this.details.forEach((details) => {
      this.syncExpandedState(details);
      details.addEventListener('toggle', () => {
        if (details.open) this.closeMenus(details);
        this.syncExpandedState(details);
      }, { signal: this.abortController.signal });
    });
    this.addEventListener('click', (event) => {
      const summary = event.target.closest('summary');
      if (summary) {
        const current = summary.parentElement;
        this.closeMenus(current);
        return;
      }
      if (event.target.closest('a')) this.closeMenus();
    }, { signal: this.abortController.signal });
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.closeMenus();
    }, { capture: true, signal: this.abortController.signal });
    document.addEventListener('focusin', (event) => {
      if (!this.contains(event.target)) this.closeMenus();
    }, { signal: this.abortController.signal });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openDetails = this.querySelector('details[open]');
      if (!openDetails) return;
      const summary = openDetails.querySelector(':scope > summary');
      this.closeMenus();
      summary?.focus();
      event.preventDefault();
    }, { signal: this.abortController.signal });
    this.show();
  }

  readScrollTop() {
    const pageWrapper = document.querySelector('.page-wrapper');
    const values = [window.scrollY, document.documentElement?.scrollTop, document.body?.scrollTop, pageWrapper?.scrollTop]
      .filter((value) => Number.isFinite(value));
    return Math.max(0, ...values);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
  }
}

if (!customElements.get('seed-header')) customElements.define('seed-header', SeedHeader);
